import { ThemeEvents } from '@theme/events';

/** @typedef {{ id: number, available: boolean, options: string[] }} KmVariant */

/**
 * @typedef {Object} KmFilterConfig
 * @property {boolean} hideNonexistent
 * @property {boolean} hideUnavailable
 * @property {'first_allowed' | 'minimal_change'} onConflict
 */

/** @type {KmFilterConfig} */
const DEFAULT_CONFIG = {
  hideNonexistent: true,
  hideUnavailable: true,
  onConflict: 'first_allowed',
};

/** @param {string | undefined} raw */
function parseConfig(raw) {
  if (!raw) return { ...DEFAULT_CONFIG };

  try {
    const parsed = JSON.parse(raw);
    return {
      hideNonexistent: parsed.hideNonexistent !== false,
      hideUnavailable: parsed.hideUnavailable !== false,
      onConflict: parsed.onConflict === 'minimal_change' ? 'minimal_change' : 'first_allowed',
    };
  } catch {
    return { ...DEFAULT_CONFIG };
  }
}

/** @param {HTMLElement} picker */
function getVariantIndex(picker) {
  const script = picker.querySelector('script[data-km-variant-index]');
  if (!script?.textContent) return [];

  try {
    const variants = JSON.parse(script.textContent);
    return Array.isArray(variants) ? variants : [];
  } catch {
    return [];
  }
}

/** @param {HTMLElement} picker */
function getSelects(picker) {
  return Array.from(picker.querySelectorAll('.variant-option--dropdowns select.variant-option__select'));
}

/**
 * @param {KmVariant[]} variants
 * @param {KmFilterConfig} config
 * @param {string[]} selectedPrefix
 * @param {number} position
 */
function getAllowedValuesAt(variants, config, selectedPrefix, position) {
  let matching = variants.filter((variant) => {
    for (let index = 0; index < selectedPrefix.length; index += 1) {
      if (variant.options[index] !== selectedPrefix[index]) return false;
    }
    return true;
  });

  if (config.hideUnavailable) {
    matching = matching.filter((variant) => variant.available);
  }

  /** @type {Set<string>} */
  const values = new Set();
  for (const variant of matching) {
    if (variant.options[position] != null) {
      values.add(variant.options[position]);
    }
  }

  return values;
}

/**
 * @param {HTMLSelectElement} select
 * @param {Set<string>} allowed
 * @param {KmFilterConfig} config
 */
function filterSelectOptions(select, allowed, config) {
  if (!config.hideNonexistent) return;

  for (const option of Array.from(select.options)) {
    if (!allowed.has(option.value)) {
      option.remove();
    }
  }
}

/**
 * @param {HTMLSelectElement} select
 * @param {string} value
 */
function setSelectValue(select, value) {
  select.value = value;

  for (const option of select.options) {
    if (option.value === value) {
      option.setAttribute('selected', 'selected');
    } else {
      option.removeAttribute('selected');
    }
  }
}

/** @param {HTMLElement} picker */
function applyDependentOptions(picker) {
  if (picker.dataset.kmDependentApplying === 'true') return;

  const config = parseConfig(picker.dataset.kmFilterConfig);
  const variants = getVariantIndex(picker);
  if (!variants.length) return;

  const selects = getSelects(picker);
  if (!selects.length) return;

  picker.dataset.kmDependentApplying = 'true';

  try {
    /** @type {string[]} */
    const selected = selects.map((select) => select.value);
    let fixSelectIndex = -1;

    for (let position = 0; position < selects.length; position += 1) {
      const prefix = selected.slice(0, position);
      const allowed = getAllowedValuesAt(variants, config, prefix, position);

      filterSelectOptions(selects[position], allowed, config);

      if (config.hideNonexistent && !allowed.has(selects[position].value)) {
        const firstAllowed = Array.from(selects[position].options).find((option) => allowed.has(option.value));

        if (firstAllowed) {
          fixSelectIndex = position;
          selected[position] = firstAllowed.value;
          setSelectValue(selects[position], firstAllowed.value);
        }
      }
    }

    if (fixSelectIndex >= 0) {
      selects[fixSelectIndex].dispatchEvent(new Event('change', { bubbles: true }));
    }
  } finally {
    delete picker.dataset.kmDependentApplying;
  }
}

/** @param {Element} picker */
function initPicker(picker) {
  if (!(picker instanceof HTMLElement)) return;
  if (picker.dataset.kmDependentOptions !== 'true') return;

  applyDependentOptions(picker);
}

function initAll() {
  document.querySelectorAll('variant-picker[data-km-dependent-options]').forEach(initPicker);
}

/** @param {Event} event */
function handleVariantUpdate(event) {
  if (!(event.target instanceof HTMLElement)) return;

  const picker = event.target.matches('variant-picker[data-km-dependent-options]')
    ? event.target
    : event.target.querySelector('variant-picker[data-km-dependent-options]');

  if (picker instanceof HTMLElement) {
    initPicker(picker);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

document.addEventListener(ThemeEvents.variantUpdate, handleVariantUpdate);
